import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Canvas, type ThreeEvent, useFrame, useThree } from "@react-three/fiber";
import { Billboard, MapControls, OrthographicCamera } from "@react-three/drei";
import { Bloom, EffectComposer, Vignette } from "@react-three/postprocessing";
import * as THREE from "three";
import {
  BUILDING_DEFS,
  CELL_SIZE,
  GRID_HEIGHT,
  GRID_WIDTH,
  UNIT_DEFS,
  canPlaceBuilding,
  gridToWorld,
  type Building,
  type CargoPacket,
  type ResourceNode,
  type Unit,
} from "../game";
import { useGameStore, type VisualEvent } from "../store/gameStore";
import { BuildingModel, CargoModel, ResourceNodeModel, UnitModel } from "./models";

const PLAYER = "#45f3e1";
const ENEMY = "#ff6f61";
const BONE = "#d9d0b7";

function GameLoop() {
  const advance = useGameStore((state) => state.advance);
  useFrame((_, dt) => advance(dt));
  return null;
}

function CameraRig() {
  const { camera, size } = useThree((state) => ({ camera: state.camera, size: state.size }));
  const controls = useRef<any>(null);
  const keys = useRef(new Set<string>());
  const fitZoom = Math.min(38, Math.max(20, Math.min(
    size.width / (GRID_WIDTH * CELL_SIZE + 3.2),
    size.height / (GRID_HEIGHT * CELL_SIZE + 5.5),
  )));

  useEffect(() => {
    const down = (event: KeyboardEvent) => keys.current.add(event.key.toLowerCase());
    const up = (event: KeyboardEvent) => keys.current.delete(event.key.toLowerCase());
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  useFrame((_, dt) => {
    if (!controls.current) return;
    const move = new THREE.Vector3();
    if (keys.current.has("w") || keys.current.has("arrowup")) move.z -= 1;
    if (keys.current.has("s") || keys.current.has("arrowdown")) move.z += 1;
    if (keys.current.has("a") || keys.current.has("arrowleft")) move.x -= 1;
    if (keys.current.has("d") || keys.current.has("arrowright")) move.x += 1;
    if (move.lengthSq() > 0) {
      move.normalize().multiplyScalar(dt * 8);
      camera.position.add(move);
      controls.current.target.add(move);
    }
    const target = controls.current.target as THREE.Vector3;
    const clampedX = THREE.MathUtils.clamp(target.x, -10, 10);
    const clampedZ = THREE.MathUtils.clamp(target.z, -6, 6);
    camera.position.x += clampedX - target.x;
    camera.position.z += clampedZ - target.z;
    target.set(clampedX, 0, clampedZ);
    controls.current.update();
  });

  return (
    <>
      <OrthographicCamera makeDefault position={[0, 17, 17]} zoom={fitZoom} near={0.1} far={100} onUpdate={(self) => self.lookAt(0, 0, 0)} />
      <MapControls
        ref={controls}
        makeDefault
        target={[0, 0, 0]}
        enableDamping
        dampingFactor={0.08}
        minZoom={18}
        maxZoom={72}
        minPolarAngle={0.52}
        maxPolarAngle={1.15}
        screenSpacePanning={false}
        mouseButtons={{ LEFT: THREE.MOUSE.PAN, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.ROTATE }}
      />
    </>
  );
}

function GridSurface() {
  const ref = useRef<THREE.InstancedMesh>(null);
  const game = useGameStore((state) => state.game);
  const buildTool = useGameStore((state) => state.buildTool);
  const hoverCell = useGameStore((state) => state.hoverCell);
  const placeAt = useGameStore((state) => state.placeAt);
  const selectEntity = useGameStore((state) => state.selectEntity);
  const count = GRID_WIDTH * GRID_HEIGHT;

  useLayoutEffect(() => {
    if (!ref.current) return;
    const matrix = new THREE.Matrix4();
    for (let gz = 0; gz < GRID_HEIGHT; gz += 1) {
      for (let gx = 0; gx < GRID_WIDTH; gx += 1) {
        const index = gz * GRID_WIDTH + gx;
        const world = gridToWorld(gx, gz);
        matrix.makeTranslation(world.x, 0, world.z);
        ref.current.setMatrixAt(index, matrix);
        const centerDistance = Math.abs(gx - (GRID_WIDTH - 1) / 2);
        const tint = gx < 8 ? new THREE.Color("#12272a") : gx > 10 ? new THREE.Color("#2a1819") : new THREE.Color("#182124");
        tint.multiplyScalar(0.8 + ((gx + gz) % 2) * 0.055 + centerDistance * 0.002);
        ref.current.setColorAt(index, tint);
      }
    }
    ref.current.instanceMatrix.needsUpdate = true;
    if (ref.current.instanceColor) ref.current.instanceColor.needsUpdate = true;
  }, []);

  const cellFromEvent = (event: Pick<ThreeEvent<PointerEvent | MouseEvent>, "instanceId">) => {
    if (event.instanceId === undefined) return null;
    return { gx: event.instanceId % GRID_WIDTH, gz: Math.floor(event.instanceId / GRID_WIDTH) };
  };

  return (
    <instancedMesh
      ref={ref}
      args={[undefined, undefined, count]}
      receiveShadow
      onPointerMove={(event) => {
        event.stopPropagation();
        const cell = cellFromEvent(event);
        if (cell) hoverCell(cell);
      }}
      onPointerOut={() => hoverCell(null)}
      onClick={(event) => {
        event.stopPropagation();
        const cell = cellFromEvent(event);
        if (!cell || event.delta > 5) return;
        if (buildTool) placeAt(cell.gx, cell.gz);
        else {
          const building = game.buildings.find((item) => !item.destroyed && item.gx === cell.gx && item.gz === cell.gz);
          selectEntity(building?.id ?? null);
        }
      }}
    >
      <boxGeometry args={[CELL_SIZE * 0.91, 0.075, CELL_SIZE * 0.91]} />
      <meshStandardMaterial vertexColors roughness={0.92} metalness={0.02} />
    </instancedMesh>
  );
}

function ArenaDecor() {
  const rocks = useMemo(() => Array.from({ length: 48 }, (_, index) => {
    const side = index % 2 === 0 ? -1 : 1;
    const t = ((index * 37) % 101) / 100;
    return {
      x: side * (14.4 + ((index * 13) % 17) * 0.11),
      z: -8.4 + t * 16.8,
      s: 0.18 + ((index * 7) % 9) * 0.045,
      r: index * 0.77,
    };
  }), []);

  return (
    <group>
      <mesh position={[0, -0.16, 0]} receiveShadow>
        <boxGeometry args={[GRID_WIDTH * CELL_SIZE + 2.8, 0.34, GRID_HEIGHT * CELL_SIZE + 2.8]} />
        <meshStandardMaterial color="#090f12" roughness={0.97} />
      </mesh>
      {[-CELL_SIZE * 2, 0, CELL_SIZE * 2].map((z, index) => (
        <mesh key={z} position={[0, -0.035, z]} receiveShadow>
          <boxGeometry args={[GRID_WIDTH * CELL_SIZE, 0.055, CELL_SIZE * 0.58]} />
          <meshStandardMaterial color={index === 1 ? "#171d1f" : "#131a1d"} emissive={index === 1 ? "#1d2626" : "#101718"} emissiveIntensity={0.22} roughness={0.76} />
        </mesh>
      ))}
      <mesh position={[0, 0.055, 0]}>
        <boxGeometry args={[0.065, 0.12, GRID_HEIGHT * CELL_SIZE]} />
        <meshStandardMaterial color="#8b6740" emissive="#3e2c1d" emissiveIntensity={0.5} />
      </mesh>
      {Array.from({ length: 12 }, (_, index) => {
        const x = -12.3 + index * 2.24;
        return [-8.35, 8.35].map((z) => (
          <group key={`${index}:${z}`} position={[x, 0, z]}>
            <mesh position={[0, 0.24, 0]} rotation={[0, 0, index % 2 ? 0.12 : -0.12]} castShadow>
              <coneGeometry args={[0.09, 0.64, 5]} />
              <meshStandardMaterial color={BONE} roughness={0.78} />
            </mesh>
            <mesh position={[0, 0.02, 0]}>
              <cylinderGeometry args={[0.18, 0.23, 0.08, 6]} />
              <meshStandardMaterial color="#171e20" roughness={0.9} />
            </mesh>
          </group>
        ));
      })}
      {rocks.map((rock, index) => (
        <mesh key={index} position={[rock.x, rock.s * 0.5 - 0.05, rock.z]} scale={[rock.s * 1.3, rock.s, rock.s]} rotation={[rock.r * 0.2, rock.r, 0]} castShadow>
          <dodecahedronGeometry args={[1, 0]} />
          <meshStandardMaterial color={index % 3 === 0 ? "#2a2b28" : "#1c2528"} roughness={0.94} />
        </mesh>
      ))}
    </group>
  );
}

function NodeView({ node }: { node: ResourceNode }) {
  const buildTool = useGameStore((state) => state.buildTool);
  const hoverCell = useGameStore((state) => state.hoverCell);
  const placeAt = useGameStore((state) => state.placeAt);
  const position = gridToWorld(node.gx, node.gz);
  return (
    <group
      position={[position.x, 0.05, position.z]}
      onPointerMove={(event) => { event.stopPropagation(); hoverCell({ gx: node.gx, gz: node.gz }); }}
      onClick={(event) => { event.stopPropagation(); if (buildTool && event.delta < 5) placeAt(node.gx, node.gz); }}
    >
      <ResourceNodeModel kind={node.kind} />
    </group>
  );
}

function HealthBar({ ratio, color = PLAYER, height = 1.2 }: { ratio: number; color?: string; height?: number }) {
  return (
    <Billboard position={[0, height, 0]} follow>
      <mesh position={[0, 0, 0]}>
        <planeGeometry args={[0.76, 0.075]} />
        <meshBasicMaterial color="#101718" transparent opacity={0.9} depthTest={false} />
      </mesh>
      <mesh position={[-0.375 + Math.max(0, ratio) * 0.375, 0, 0.002]} scale={[Math.max(0.001, ratio), 1, 1]}>
        <planeGeometry args={[0.72, 0.042]} />
        <meshBasicMaterial color={color} toneMapped={false} depthTest={false} />
      </mesh>
    </Billboard>
  );
}

function BuildingView({ building }: { building: Building }) {
  const selectedId = useGameStore((state) => state.selectedId);
  const selectEntity = useGameStore((state) => state.selectEntity);
  const hoverCell = useGameStore((state) => state.hoverCell);
  const game = useGameStore((state) => state.game);
  const group = useRef<THREE.Group>(null);
  const mountedAt = useRef(performance.now());
  const duration = building.type === "incubator" && building.recipe
    ? UNIT_DEFS[building.recipe].buildTime
    : BUILDING_DEFS[building.type].productionTime ?? 1;
  const progress = building.productionProgress / duration;
  const selected = selectedId === building.id;

  useFrame(() => {
    if (!group.current) return;
    const age = (performance.now() - mountedAt.current) / 260;
    const scale = THREE.MathUtils.clamp(1 - Math.pow(1 - Math.min(1, age), 3), 0.02, 1);
    group.current.scale.set(scale, scale, scale);
  });

  return (
    <group
      ref={group}
      position={[building.x, 0.075, building.z]}
      onPointerMove={(event) => { event.stopPropagation(); hoverCell({ gx: building.gx, gz: building.gz }); }}
      onClick={(event) => { event.stopPropagation(); if (event.delta < 5) selectEntity(building.id); }}
    >
      <BuildingModel
        type={building.type}
        owner={building.owner}
        selected={selected}
        powered={building.type === "heartforge" || (building.powered && building.connected && !building.paused)}
        progress={progress}
        stockCount={building.garrison.length}
      />
      {(selected || building.hp < building.maxHp) && (
        <HealthBar ratio={building.hp / building.maxHp} color={building.owner === "player" ? PLAYER : ENEMY} height={building.type === "heartforge" ? 1.35 : 1.06} />
      )}
      {selected && building.type === "spineTurret" && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
          <ringGeometry args={[CELL_SIZE * 3.55, CELL_SIZE * 3.6, 64]} />
          <meshBasicMaterial color={PLAYER} transparent opacity={0.13} depthWrite={false} />
        </mesh>
      )}
      {game.phase !== "playing" && building.type === "heartforge" && building.hp <= 0 && <RuptureCore color={building.owner === "player" ? PLAYER : ENEMY} />}
    </group>
  );
}

function UnitView({ unit }: { unit: Unit }) {
  const group = useRef<THREE.Group>(null);
  const definition = UNIT_DEFS[unit.type];
  useFrame((_, dt) => {
    if (!group.current) return;
    const factor = 1 - Math.exp(-dt * 14);
    group.current.position.x = THREE.MathUtils.lerp(group.current.position.x, unit.x, factor);
    group.current.position.z = THREE.MathUtils.lerp(group.current.position.z, unit.z, factor);
  });
  return (
    <group ref={group} position={[unit.x, 0.08, unit.z]}>
      <UnitModel type={unit.type} owner={unit.owner} attacking={unit.attackCooldown > definition.attackCooldown * 0.72} />
      {unit.hp < unit.maxHp && <HealthBar ratio={unit.hp / unit.maxHp} color={unit.owner === "player" ? PLAYER : ENEMY} height={0.94} />}
    </group>
  );
}

function CargoView({ packet }: { packet: CargoPacket }) {
  const group = useRef<THREE.Group>(null);
  useFrame(({ clock }, dt) => {
    if (!group.current) return;
    const factor = 1 - Math.exp(-dt * 18);
    group.current.position.x = THREE.MathUtils.lerp(group.current.position.x, packet.x, factor);
    group.current.position.z = THREE.MathUtils.lerp(group.current.position.z, packet.z, factor);
    group.current.position.y = 0.38 + Math.sin(clock.elapsedTime * 7 + packet.id.length) * 0.055;
    group.current.rotation.y += dt * 2.8;
  });
  return <group ref={group} position={[packet.x, 0.38, packet.z]}><CargoModel kind={packet.kind} owner={packet.owner} /></group>;
}

function Link({ from, to, color }: { from: { x: number; z: number }; to: { x: number; z: number }; color: string }) {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const length = Math.hypot(dx, dz);
  return (
    <mesh position={[(from.x + to.x) / 2, 0.19, (from.z + to.z) / 2]} rotation={[0, -Math.atan2(dz, dx), 0]}>
      <boxGeometry args={[length, 0.035, 0.045]} />
      <meshBasicMaterial color={color} transparent opacity={0.72} toneMapped={false} />
    </mesh>
  );
}

function SupplyNetwork() {
  const buildings = useGameStore((state) => state.game.buildings);
  const lens = useGameStore((state) => state.supplyLens);
  if (!lens) return null;
  const active = buildings.filter((building) => !building.destroyed && building.connected);
  const pairs: Array<[Building, Building]> = [];
  for (let i = 0; i < active.length; i += 1) {
    for (let j = i + 1; j < active.length; j += 1) {
      if (active[i].owner === active[j].owner && Math.abs(active[i].gx - active[j].gx) + Math.abs(active[i].gz - active[j].gz) === 1) pairs.push([active[i], active[j]]);
    }
  }
  return <group>{pairs.map(([a, b]) => <Link key={`${a.id}:${b.id}`} from={a} to={b} color={a.owner === "player" ? PLAYER : ENEMY} />)}</group>;
}

function PlacementGhost() {
  const game = useGameStore((state) => state.game);
  const tool = useGameStore((state) => state.buildTool);
  const cell = useGameStore((state) => state.hoveredCell);
  if (!tool || !cell) return null;
  const world = gridToWorld(cell.gx, cell.gz);
  const valid = canPlaceBuilding(game, "player", tool, cell.gx, cell.gz).ok;
  const color = valid ? PLAYER : "#ff4d43";
  return (
    <group position={[world.x, 0.1, world.z]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.5, 0.59, 8]} />
        <meshBasicMaterial color={color} transparent opacity={0.8} toneMapped={false} depthWrite={false} />
      </mesh>
      <mesh position={[0, 0.32, 0]}>
        <boxGeometry args={[0.86, 0.62, 0.86]} />
        <meshBasicMaterial color={color} transparent opacity={0.16} wireframe />
      </mesh>
    </group>
  );
}

function Beam({ from, to, color }: { from: THREE.Vector3; to: THREE.Vector3; color: string }) {
  const direction = new THREE.Vector3().subVectors(to, from);
  const midpoint = new THREE.Vector3().addVectors(from, to).multiplyScalar(0.5);
  const quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.clone().normalize());
  return (
    <mesh position={midpoint} quaternion={quaternion}>
      <cylinderGeometry args={[0.025, 0.055, direction.length(), 6]} />
      <meshBasicMaterial color={color} transparent opacity={0.82} toneMapped={false} depthWrite={false} />
    </mesh>
  );
}

function PulseFx({ event }: { event: VisualEvent }) {
  const group = useRef<THREE.Group>(null);
  const material = useRef<THREE.MeshBasicMaterial>(null);
  const start = useRef(performance.now());
  const color = event.owner === "ai" ? ENEMY : PLAYER;
  useFrame(() => {
    const progress = THREE.MathUtils.clamp((performance.now() - start.current) / (event.type === "destroy" ? 1050 : 440), 0, 1);
    if (group.current) {
      const scale = event.type === "destroy" ? 0.4 + progress * 3.8 : 0.25 + progress * 1.5;
      group.current.scale.setScalar(scale);
      group.current.rotation.y += 0.04;
    }
    if (material.current) material.current.opacity = (1 - progress) * (event.type === "destroy" ? 0.9 : 0.65);
  });
  return (
    <group ref={group} position={[event.x ?? 0, 0.16, event.z ?? 0]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.25, event.type === "destroy" ? 0.06 : 0.025, 6, 32]} />
        <meshBasicMaterial ref={material} color={event.type === "hit" ? "#fff1b8" : color} transparent toneMapped={false} depthWrite={false} />
      </mesh>
      {event.type === "destroy" && Array.from({ length: 7 }, (_, index) => {
        const angle = index * Math.PI * 2 / 7;
        return (
          <mesh key={index} position={[Math.cos(angle) * 0.35, 0.16 + (index % 2) * 0.1, Math.sin(angle) * 0.35]} rotation={[angle, angle * 0.4, angle]}>
            <tetrahedronGeometry args={[0.09, 0]} />
            <meshBasicMaterial color={index % 2 ? BONE : color} transparent opacity={0.78} toneMapped={false} />
          </mesh>
        );
      })}
    </group>
  );
}

function CombatEffects() {
  const effects = useGameStore((state) => state.effects);
  const game = useGameStore((state) => state.game);
  const entities = [...game.buildings, ...game.units];
  return (
    <group>
      {effects.map((event) => {
        if (event.type === "attack" && event.targetId) {
          const target = entities.find((entity) => entity.id === event.targetId);
          if (target) {
            return <Beam key={event.id} from={new THREE.Vector3(event.x ?? 0, 0.72, event.z ?? 0)} to={new THREE.Vector3(target.x, 0.45, target.z)} color={event.owner === "ai" ? ENEMY : PLAYER} />;
          }
        }
        return <PulseFx key={event.id} event={event} />;
      })}
    </group>
  );
}

function RuptureCore({ color }: { color: string }) {
  const ref = useRef<THREE.Group>(null);
  useFrame(({ clock }) => {
    if (!ref.current) return;
    ref.current.rotation.y = clock.elapsedTime * 1.8;
    const scale = 1 + Math.sin(clock.elapsedTime * 3) * 0.12;
    ref.current.scale.setScalar(scale);
  });
  return (
    <group ref={ref} position={[0, 0.35, 0]}>
      {[0.4, 0.7, 1].map((size, index) => (
        <mesh key={size} rotation={[Math.PI / 2 + index * 0.4, index, 0]}>
          <torusGeometry args={[size, 0.025, 5, 40]} />
          <meshBasicMaterial color={color} transparent opacity={0.55 - index * 0.12} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

function World() {
  const game = useGameStore((state) => state.game);
  return (
    <>
      <ArenaDecor />
      <GridSurface />
      {game.resourceNodes.map((node) => <NodeView key={node.id} node={node} />)}
      <SupplyNetwork />
      {game.buildings.filter((building) => !building.destroyed).map((building) => <BuildingView key={building.id} building={building} />)}
      {game.units.map((unit) => <UnitView key={unit.id} unit={unit} />)}
      {game.cargoPackets.map((packet) => <CargoView key={packet.id} packet={packet} />)}
      <PlacementGhost />
      <CombatEffects />
    </>
  );
}

export function GameScene() {
  return (
    <Canvas
      className="game-canvas"
      shadows
      dpr={[1, 1.65]}
      gl={{ antialias: true, powerPreference: "high-performance", alpha: false }}
      onCreated={({ gl }) => {
        gl.outputColorSpace = THREE.SRGBColorSpace;
        gl.toneMapping = THREE.ACESFilmicToneMapping;
        gl.toneMappingExposure = 1.08;
        gl.shadowMap.type = THREE.PCFSoftShadowMap;
        gl.domElement.addEventListener("webglcontextlost", (event) => {
          event.preventDefault();
          useGameStore.getState().notify("Renderer interrupted", "WebGL context lost; attempting recovery.", true);
        });
        gl.domElement.addEventListener("webglcontextrestored", () => useGameStore.getState().notify("Renderer restored", "The amberline is visible again."));
      }}
    >
      <color attach="background" args={["#071013"]} />
      <fog attach="fog" args={["#071013", 24, 49]} />
      <ambientLight intensity={0.76} color="#b7d7d1" />
      <hemisphereLight args={["#8fcac5", "#2d1715", 1.1]} />
      <directionalLight position={[-8, 18, 10]} intensity={2.3} color="#fff0d1" castShadow shadow-mapSize={[2048, 2048]} shadow-camera-left={-19} shadow-camera-right={19} shadow-camera-top={13} shadow-camera-bottom={-13} shadow-bias={-0.0003} />
      <pointLight position={[-11, 3, 0]} color={PLAYER} intensity={12} distance={11} decay={2} />
      <pointLight position={[11, 3, 0]} color={ENEMY} intensity={12} distance={11} decay={2} />
      <GameLoop />
      <CameraRig />
      <World />
      <EffectComposer multisampling={0}>
        <Bloom mipmapBlur intensity={0.62} luminanceThreshold={0.58} luminanceSmoothing={0.32} />
        <Vignette eskil={false} offset={0.2} darkness={0.66} />
      </EffectComposer>
    </Canvas>
  );
}
